<script setup lang="ts">
// Vue
import { ref, onUnmounted } from "vue";
import { ClipboardCheck, ClipboardList } from "@lucide/vue";

// Dependencies
import JSConfetty from "js-confetti";

// Providers
import { useCreateBuildingProject, NewBuildingProjectType } from "@/features/BuildingProject";

// Utils
import { useRouter } from "vue-router";

const router = useRouter();

// Components
import Button from "@/components/Reusables/Buttons/Button.vue";
import InviteModal from "@/components/Utils/InviteUserToProject/InviteModal.vue";

const confetti = new JSConfetty();
confetti.addConfetti({
  emojis: ["🍃"],
  emojiSize: 50,
  confettiNumber: 120,
});

const createBuildingProject = useCreateBuildingProject();

const project = ref<NewBuildingProjectType>(createBuildingProject.getProject());
const wasCopied = ref<boolean>(false);
const inviteUsersModalIsHidden = ref<boolean>(true);
const url = ref<string>(import.meta.env.VITE_APIMANAGER_BASEURL + "/streams/" + project.value.streamId);

const copyToClipboard = () => {
  if (!project.value.streamId) return;
  navigator.clipboard.writeText(url.value);
  wasCopied.value = true;
};

const toggleInviteUsersModal = () => {
  inviteUsersModalIsHidden.value = !inviteUsersModalIsHidden.value;
};

const continueRedirect = () => {
  router.push({
    name: "projectBreakdown",
    params: { id: createBuildingProject.getBuildingProjectId() },
  });
};

onUnmounted(() => {
  createBuildingProject.reset();
});
</script>

<template>
  <div class="flex flex-col items-center space-y-6">
    <!-- "Project created", not "Ready to save Gaia?" (RTLCA create-wizard audit, finding F5): the
         project already exists by the time this screen renders, so the old heading asked a question
         whose answer was already yes. The name underneath is what actually distinguishes this screen
         from the next project's. -->
    <div class="flex flex-col items-center gap-1">
      <h1 class="t-sh1">Project created</h1>
      <p class="t-h4 text-type-default">{{ project.projectName }}</p>
    </div>

    <!-- One per-choice sentence, only for the branch that needs disambiguating, then the same next-steps
         line for both: a fresh, blank project either way, so the encouragement to go fill it in applies
         whether or not a model is connected. -->
    <p
      v-if="!project.streamId"
      class="t-body text-type-muted max-w-[480px] text-center">
      This project has no 3D model connection, so constructions and quantities are entered manually.
    </p>
    <p class="t-body text-type-muted max-w-[480px] text-center">
      Your project is set up and saved. Next, add your constructions and materials in Material Mapping, or invite your team to
      work with you.
    </p>

    <div
      v-if="project.streamId"
      class="flex flex-col gap-8">
      <button
        type="button"
        data-cy="copy-streamid-btn"
        aria-label="Copy stream URL to clipboard"
        class="text-type-default focus-ring rounded-8 bg-slate-0 t-small flex cursor-pointer items-center justify-center gap-4 border border-slate-200 px-6 py-3 font-bold shadow-xs"
        @click="copyToClipboard">
        <span>{{ url }}</span>
        <ClipboardList
          v-if="!wasCopied"
          class="icon-sm shrink-0"
          :stroke-width="1.5"
          aria-hidden="true" />
        <ClipboardCheck
          v-else
          class="icon-sm text-app-green-500 shrink-0"
          :stroke-width="1.5"
          aria-hidden="true" />
      </button>

      <div class="t-caption flex flex-col gap-2 text-center">
        <span>Copy and paste the URL into the Speckle Connector of your choice and get started! </span>
      </div>
    </div>
  </div>

  <!-- The screen's actions live in the pinned bar's slots like every other step's (finding 6),
       teleported so the handlers stay here. `defer`, because this screen mounts before the bar. -->
  <Teleport
    defer
    to="#wizard-bar-custom-right">
    <!-- `color="button"` rendered `bg-slate-200 text-slate-600`, one shade off `color="disabled"`'s
         `bg-slate-200 text-type-muted` — close enough that this read as an inactive control rather than
         a real secondary action (RTLCA create-wizard audit, finding F5). `inline` is the reusable's
         actual low-emphasis-but-enabled variant: bordered, no fill, text-like next to the primary CTA. -->
    <Button
      label="Invite users"
      color="inline"
      @click="toggleInviteUsersModal" />
    <Button
      label="Go to Project"
      color="primary"
      @click="continueRedirect" />
  </Teleport>

  <InviteModal
    v-if="createBuildingProject.getBuildingProjectId() && !inviteUsersModalIsHidden"
    :project="project"
    :user-permissions="['project.delete']"
    :building-project-id="createBuildingProject.getBuildingProjectId()"
    @toggle-invite-modal="toggleInviteUsersModal" />
</template>
